// src/api/api.js
//
// Central API config + global axios interceptors.
//
// Auth flow (token refresh):
//   - Backend issues a short-lived access token (15 min) on /login. We keep
//     it in memory + localStorage so existing `import axios from 'axios'`
//     callers get the Bearer header for free.
//   - Backend also sets a long-lived refresh token in an httpOnly cookie.
//     The cookie is the only piece JS can't read — that's the security
//     boundary. We never see/store it.
//   - This module silently rotates the access token before it expires
//     (proactive timer), and on any 401 it tries one refresh + retries the
//     failing request (reactive). Concurrent failed requests share a single
//     refresh promise so we don't stampede.
//   - Refresh failure is the only thing that takes the user to /login.

import axios from "axios";

export const API_URL = process.env.REACT_APP_API_URL || "https://hub.electronikmedia.com";
export const UPLOAD_URL = process.env.REACT_APP_UPLOAD_URL || "https://hub.electronikmedia.com/backend";

/* ---------- Token storage helpers ----------------------------------- */

// `token` is the access token; we read it from localStorage on each request
// so other tabs (BroadcastChannel + storage event below) stay in sync.
const TOKEN_KEY = "token";

export const getAccessToken = () =>
  typeof window === "undefined" ? null : localStorage.getItem(TOKEN_KEY);

export const setAccessToken = (token) => {
  if (typeof window === "undefined") return;
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
  scheduleProactiveRefresh(token);
};

// Decode a JWT *without* a library — just base64-url decode the middle segment
// to read `exp`. We never trust this for auth; it's only used to schedule a
// refresh and for "is this token about to expire" hints.
function decodeJwtPayload(jwt) {
  if (!jwt || typeof jwt !== "string") return null;
  const parts = jwt.split(".");
  if (parts.length !== 3) return null;
  try {
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const json = atob(b64.padEnd(Math.ceil(b64.length / 4) * 4, "="));
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/** seconds until the access token expires; null if no/invalid token */
export function secondsUntilExpiry(token = getAccessToken()) {
  const p = decodeJwtPayload(token);
  if (!p?.exp) return null;
  return p.exp - Math.floor(Date.now() / 1000);
}

/* ---------- Refresh state machine ----------------------------------- */

// Single in-flight refresh promise — every concurrent failure awaits this so
// we only hit /refresh once per token expiry burst.
let refreshPromise = null;
// We've already given up and bounced once; don't loop.
let redirecting = false;

async function callRefresh() {
  // Use a bare axios instance so the request doesn't go through our own
  // interceptors (which would otherwise try to attach a stale token and
  // could recurse on 401).
  const res = await axios.post(
    `${API_URL}/api/auth/refresh`,
    {},
    { withCredentials: true },
  );
  const newToken = res.data?.token;
  if (!newToken) throw new Error("refresh returned no token");
  setAccessToken(newToken);

  // Keep auxiliary client state in sync (some pages read these on mount).
  if (res.data.user) {
    try {
      localStorage.setItem("userdata", JSON.stringify(res.data.user));
    } catch { }
  }
  if (res.data.role) localStorage.setItem("role", res.data.role);
  if (res.data.permissions) {
    try {
      localStorage.setItem(
        "permissions",
        JSON.stringify(res.data.permissions || []),
      );
    } catch { }
  }
  return newToken;
}

/** Public entry point — coalesces concurrent callers onto one in-flight promise. */
export function refreshAccessToken() {
  if (!refreshPromise) {
    refreshPromise = callRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

/* ---------- Proactive refresh timer --------------------------------- */
//
// Schedule a silent refresh 60s before the access token expires. This means
// during normal use the user never even sees a 401 retry — the token just
// rotates in the background.

let proactiveTimer = null;

export function scheduleProactiveRefresh(token = getAccessToken()) {
  if (proactiveTimer) {
    clearTimeout(proactiveTimer);
    proactiveTimer = null;
  }
  if (!token) return;
  const secs = secondsUntilExpiry(token);
  if (secs == null) return;
  // 60s buffer; if we're already inside the buffer, fire immediately on
  // a microtask so we don't block the caller.
  const delayMs = Math.max(0, (secs - 60) * 1000);
  proactiveTimer = setTimeout(() => {
    refreshAccessToken().catch(() => {
      // If proactive refresh fails (e.g. refresh cookie expired), the
      // next request will 401 and the reactive path takes the user to
      // /login. We don't bounce here to avoid surprising the user.
    });
  }, delayMs);
}

/* ---------- Hard logout (all paths converge here) ------------------- */

export async function hardLogout({ silent = false } = {}) {
  if (proactiveTimer) clearTimeout(proactiveTimer);
  proactiveTimer = null;
  refreshPromise = null;
  // Best-effort server-side revocation. Don't await on the redirect path.
  try {
    await axios.post(
      `${API_URL}/api/auth/logout`,
      {},
      { withCredentials: true },
    );
  } catch { }
  try {
    localStorage.clear();
  } catch { }
  if (!silent && !redirecting && typeof window !== "undefined") {
    redirecting = true;
    window.location.assign("/login");
  }
}

/* ---------- Axios interceptors -------------------------------------- */

// Send credentials by default so the refresh cookie ride-alongs work for
// /api/auth/refresh and /api/auth/logout.
axios.defaults.withCredentials = true;

axios.interceptors.request.use(
  (config) => {
    // Skip if the caller already set their own Authorization header.
    const hasAuth =
      config.headers &&
      (config.headers.Authorization || config.headers.authorization);
    if (!hasAuth) {
      const token = getAccessToken();
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Endpoints we should NOT auto-retry/refresh on (would either cause a loop
// or are themselves the source of truth for a 401).
const SKIP_RETRY = /\/api\/auth\/(login|refresh|logout|register)/;

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;
    const cfg = error?.config || {};
    const url = cfg.url || "";

    // Pass through anything that isn't a 401 we can act on.
    if (status !== 401 || !cfg || cfg.__retried || SKIP_RETRY.test(url)) {
      // Special case: refresh itself failed → user must re-login.
      if (status === 401 && /\/api\/auth\/refresh/.test(url) && !redirecting) {
        hardLogout();
      }
      return Promise.reject(error);
    }

    // Try one silent refresh, then replay the original request.
    cfg.__retried = true;
    try {
      const newToken = await refreshAccessToken();
      cfg.headers = cfg.headers || {};
      cfg.headers.Authorization = `Bearer ${newToken}`;
      return axios(cfg);
    } catch (refreshErr) {
      // Refresh failed → session is truly dead.
      if (!redirecting) hardLogout();
      return Promise.reject(refreshErr);
    }
  },
);

/* ---------- Cross-tab sync ------------------------------------------ */
// If another tab logs the user out (clears localStorage) or refreshes (writes
// a new token), reflect it in this tab without forcing a reload.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key === TOKEN_KEY) {
      scheduleProactiveRefresh(e.newValue);
      if (!e.newValue && !redirecting) {
        redirecting = true;
        window.location.assign("/login");
      }
    }
  });
  // Boot — schedule a refresh based on whatever token we have on page load.
  scheduleProactiveRefresh();
}

export default axios;
